import { Component, h, ComponentInterface, State, Prop, Element } from '@stencil/core';
import { guid, _COLORS_ } from 'utils';


@Component({
	tag: 'kdx-basic-cta',
	styleUrl: 'kdx-basic-cta.scss',
	shadow: false
})
export class StBasicCta implements ComponentInterface {

	@Element() root: HTMLElement;

	@State() guid: string = guid();
	@State() classStr: string = 'kdx-basic-cta';
	@State() links: Array<any> = [];

	@Prop() orientation: string = 'left';
	@Prop() themeOption: string = 'cta-theme-1'; // cta-theme-1 (white bg) or cta-theme-2 (colored bg)
	@Prop() sectionLabel: string;
	@Prop() imgSrc: string;
	@Prop() imgAlt: string;
	@Prop() videoSrc: string;
	@Prop() videoHost: string = 'youtube';

	@Prop() ctaPrimaryText: string;
	@Prop() ctaPrimaryHref: string;
	@Prop() ctaPrimaryAriaLabel: string;
	@Prop() ctaPrimaryTarget: string;
	@Prop() ctaPrimaryIcon: string;
	@Prop() ctaPrimarySpeedbump: boolean = false;

	@Prop({
		mutable: true,
		reflect: true
	}) ctaPrimaryRel: string;

	@Prop() ctaSecondaryText: string;
	@Prop() ctaSecondaryHref: string;
	@Prop() ctaSecondaryAriaLabel: string;
	@Prop() ctaSecondaryTarget: string;
	@Prop() ctaSecondaryIcon: string;
	@Prop() ctaSecondarySpeedbump: boolean = false;

	@Prop({
		mutable: true,
		reflect: true
	}) ctaSecondaryRel: string;

	constructor() {
		if (this.videoSrc) {
			switch (this.videoHost) {
				case 'youtube': {
					this.videoSrc += '?modestbranding=1&autohide=1&autoplay=0';
				}
			}
		}
	}

	componentWillLoad() {
		this.getLinks();

		// set rel if component link targets are new window
		if (this.ctaPrimarySpeedbump || (this.ctaPrimaryTarget && this.ctaPrimaryTarget.toLowerCase() === '_blank')) {
			this.ctaPrimaryRel = 'noopener';
		}

		if (this.ctaSecondarySpeedbump || (this.ctaSecondaryTarget && this.ctaSecondaryTarget.toLowerCase() === '_blank')) {
			this.ctaSecondaryRel = 'noopener';
		}
	}

	componentDidRender() {
		globalThis.imageLoader?.initialize();
	}

	private getClasses = () => {
		let classStr = `${this.classStr}`;

		if (this.orientation.toLowerCase() === 'right') {
			return (`${classStr} ${classStr}__right`);
		}

		return classStr;
	}

	private getLinks() {
		const links = [];

		if (this.ctaPrimaryText != null) {
			links.push({
				value: this.ctaPrimaryText,
				href: this.ctaPrimaryHref,
				icon: this.ctaPrimaryIcon,
				target: this.ctaPrimaryTarget
			});
		}

		if (this.ctaSecondaryText != null) {
			links.push({
				value: this.ctaSecondaryText,
				href: this.ctaSecondaryHref,
				icon: this.ctaSecondaryIcon,
				target: this.ctaSecondaryTarget
			});
		}

		this.links = links;
	}

	render() {
		return (
			<section class={`${this.getClasses()} ${this.themeOption}`} id={`kdx-basic-cta-${this.guid}`} aria-label={this.sectionLabel}>
				<div class="container-fluid">
					<div class="row">
						{/** Render the IMAGE */}
						{(this.imgSrc && !this.videoSrc) &&
							<div class="col-lg-6 cta-media">
								<div class="img lazy-load-bg" role="presentation" aria-describedby={this.imgAlt} title={this.imgAlt} style={{ backgroundImage: `url('${this.imgSrc}')`}}></div>
							</div>
						}						

						{(this.videoSrc) &&
							<div class="col-lg-6 cta-media">
								<kdx-video-embed
									img-src={this.imgSrc}
									modal-id={`video-lightbox`}
									img-alt="fancy placeholder video thumbnail"									
								>
									<iframe
										width="768"
										height="432"
										data-src={this.videoSrc}
										frameborder="0"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen>
									</iframe>
								</kdx-video-embed>
							</div>
						}
						<div class="col-lg-6 cta-content">
							<article class="cta-content-inner" aria-label={`cta content for ${this.sectionLabel}`}>
								<slot />

								{(this.ctaPrimaryHref || this.ctaSecondaryHref) &&(
									<div class="ctas">
										{(this.ctaPrimaryHref) && (
											<a
												href={this.ctaPrimaryHref}
												id={`cta-1-${this.guid}`}
												class={`btn btn-primary ${this.ctaPrimarySpeedbump ? ' speedbump-link' : ''}`}
												aria-label={this.ctaPrimaryAriaLabel}
												target={this.ctaPrimaryTarget}
												rel={this.ctaPrimaryRel}>
												{this.ctaPrimaryText}{(this.ctaPrimaryIcon) && (
													<span class="svg-icon">
														<kdx-icon icon={this.ctaPrimaryIcon} />
													</span>
												)}
											</a>
										)}

										{(this.ctaSecondaryHref) && (
											<kdx-arrow-link
												href={this.ctaSecondaryHref}
												label={this.ctaSecondaryAriaLabel}
												target={this.ctaSecondaryTarget}
												is-speedbump={this.ctaSecondarySpeedbump}
											>
												{this.ctaSecondaryText}
											</kdx-arrow-link>											
										)}
									</div>
								)}
							</article>
						</div>
					</div>
				</div>
			</section>
		);
	}
}
